import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { useCatalog } from '../context/CatalogContext';
import { api } from '../lib/api';
import { TypeTheme, discountPercent, formatCount } from '../lib/types';
import { grad } from '../components/ui';

type FileLink = { title: string; url: string; viewer: 'webview' | 'browser' };

export function ProductDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getProduct, loading } = useCatalog();
  const { addToCart, isInCart, ownsProduct } = useApp();

  const product = getProduct(id);
  const owned = product ? ownsProduct(product.id) : false;
  const hasPdfTopics = !!product?.contents?.some((c) => c.hasPdf);

  const [links, setLinks] = useState<Record<number, FileLink>>({});
  const [filesLoading, setFilesLoading] = useState(false);

  useEffect(() => {
    if (!product || !owned || !hasPdfTopics) {
      setLinks({});
      return;
    }
    let cancelled = false;
    setFilesLoading(true);
    api
      .getProductFiles(product.id)
      .then((r) => {
        if (cancelled) return;
        const map: Record<number, FileLink> = {};
        r.files.forEach((f) => (map[f.index] = { title: f.title, url: f.url, viewer: f.viewer }));
        setLinks(map);
      })
      .catch(() => !cancelled && setLinks({}))
      .finally(() => !cancelled && setFilesLoading(false));
    return () => {
      cancelled = true;
    };
  }, [product, owned, hasPdfTopics]);

  if (loading && !product) {
    return <div className="loading-screen"><div className="spinner" /></div>;
  }
  if (!product) {
    return (
      <div className="col center" style={{ padding: 60, gap: 10 }}>
        <div style={{ fontSize: 40 }}>😕</div>
        <div style={{ fontWeight: 800 }}>Product not found</div>
        <button className="btn btn-primary" onClick={() => navigate('/store')}>Browse store</button>
      </div>
    );
  }

  const t = TypeTheme[product.type];
  const inCart = isInCart(product.id);
  const isPlayable = product.type === 'quiz' || product.type === 'pyq' || product.type === 'test-series';
  const off = discountPercent(product);

  function primaryAction() {
    if (isPlayable && (owned || product!.isFree)) {
      navigate(`/practice/${product!.id}`);
      return;
    }
    if (owned) return;
    if (!inCart) addToCart(product!.id);
    navigate('/cart');
  }

  const ctaLabel =
    isPlayable && (owned || product.isFree) ? 'Start practice' : owned ? 'In your library' : product.isFree ? 'Get for free' : inCart ? 'Go to cart' : 'Buy now';

  function openTopic(i: number) {
    const f = links[i];
    if (f) window.open(f.url, '_blank', 'noopener');
  }

  return (
    <div className="has-buybar" style={{ paddingTop: 20 }}>
      <div className="hero" style={{ ...grad(product.gradient), textAlign: 'center' }}>
        <div style={{ fontSize: 64 }}>{product.emoji}</div>
        <div className="row gap8 center" style={{ marginTop: 8 }}>
          <span className="badge" style={{ background: 'rgba(255,255,255,.25)', color: '#fff' }}>{t.label}</span>
          {product.bestseller && <span className="badge" style={{ background: 'rgba(255,255,255,.25)', color: '#fff' }}>🔥 Bestseller</span>}
        </div>
      </div>

      <div className="grid" style={{ gridTemplateColumns: 'minmax(0,1fr)', marginTop: 20 }}>
        <h1 style={{ fontSize: 24, fontWeight: 900 }}>{product.title}</h1>
        <p className="muted">{product.subtitle}</p>
        <div className="row gap12" style={{ fontSize: 14 }}>
          <span className="stars">★ {product.rating.toFixed(1)}</span>
          <span className="muted">({formatCount(product.ratingCount)})</span>
          <span className="muted">👥 {formatCount(product.students)} learners</span>
        </div>
        <div className="muted">By <b style={{ color: 'var(--text)' }}>{product.author}</b></div>

        <div className="row gap8 wrap" style={{ marginTop: 6 }}>
          {product.tags.map((tag) => (
            <span key={tag} className="chip" style={{ background: 'var(--primary-soft)', color: 'var(--primary)', border: 'none' }}>{tag}</span>
          ))}
        </div>

        {/* contents / topics with gated PDFs */}
        <section className="section" style={{ marginTop: 24 }}>
          <h2 className="section-title" style={{ marginBottom: 12 }}>Contents ({product.contents.length})</h2>
          {product.contents.map((c, i) => {
            const locked = !!c.hasPdf && !owned;
            const canOpen = !!c.hasPdf && owned && !!links[i];
            const clickable = locked || canOpen;
            return (
              <div
                key={c.title}
                onClick={() => (locked ? alert('Buy this pack to open the PDF.') : canOpen ? openTopic(i) : undefined)}
                className="card row gap12"
                style={{ marginBottom: 10, cursor: clickable ? 'pointer' : 'default', border: c.hasPdf ? '1px solid var(--primary-soft)' : undefined }}
              >
                <div className="center tc-num" style={{ width: 30, height: 30, borderRadius: 15, fontWeight: 800, fontSize: 14, ['--c' as string]: t.color } as React.CSSProperties}>{i + 1}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600 }}>{c.title}</div>
                  <div className="faint" style={{ fontSize: 12, marginTop: 2 }}>{c.meta}{c.hasPdf ? '  ·  PDF' : ''}</div>
                </div>
                {filesLoading && c.hasPdf && owned ? <div className="spinner" style={{ width: 18, height: 18, borderWidth: 3 }} /> : locked ? <span>🔒</span> : canOpen ? <span className="tc" style={{ ['--c' as string]: t.color } as React.CSSProperties}>↗</span> : null}
              </div>
            );
          })}
          {hasPdfTopics && !owned && <div className="muted" style={{ fontSize: 13, textAlign: 'center', marginTop: 4 }}>🔒 Buy to unlock and read the PDFs.</div>}
        </section>

        <section className="section" style={{ marginTop: 8 }}>
          <h2 className="section-title" style={{ marginBottom: 10 }}>About this pack</h2>
          <p className="muted" style={{ lineHeight: 1.6 }}>{product.description}</p>
        </section>

        <section className="section" style={{ marginTop: 8 }}>
          <h2 className="section-title" style={{ marginBottom: 10 }}>What&apos;s included</h2>
          {product.features.map((f) => (
            <div key={f} className="row gap8" style={{ marginBottom: 8 }}><span className="tc" style={{ ['--c' as string]: t.color } as React.CSSProperties}>✔</span> {f}</div>
          ))}
        </section>
      </div>

      {/* fixed buy bar */}
      <div className="buybar">
        <div className="container row" style={{ justifyContent: 'space-between', gap: 16 }}>
          <div>
            {product.isFree ? (
              <span className="free" style={{ fontSize: 22 }}>FREE</span>
            ) : (
              <>
                <span className="price" style={{ fontSize: 24 }}>₹{product.price}</span>
                {product.mrp > product.price && <span className="mrp">₹{product.mrp}</span>}
                {off > 0 && <div className="off">{off}% off · limited time</div>}
              </>
            )}
          </div>
          <button className="btn btn-primary" style={{ minWidth: 160 }} onClick={primaryAction}>{ctaLabel}</button>
        </div>
      </div>
    </div>
  );
}
